/**
 * Normalize Financial Instruction form/API body for IO.REST.MSG.FINRequest (%JSONImport).
 * Empty strings on %Integer/%Boolean fields cause IRIS ERROR #9406.
 * Mirrors property types in MBengine_ENS IO.REST.MSG.FINRequest.cls — 05/08/2026 PK.
 */

const FIN_INTEGER_FIELDS: readonly string[] = [
  'id',
  'finGid',
  'finRno',
  'rejectionBACGID',
  'rejectionBACRno',
  'instructionNo',
  'ignoreBankService',
  'forceFee',
  'maxTransDaysBeforeTransDate',
  'maxTransDaysBeforeEffDate',
  'noOfTENToCreate',
  'cardRelatedTransaction',
  'bacStatusAllocatedAction',
  'bacStatusActiveAction',
  'bacStatusLimitedAction',
  'bacStatusSuspendedAction',
  'bacStatusClosedAction',
  'bacStatusSemiDormantAction',
  'bacStatusStaleAction',
  'bciStatusInactiveAction',
  'bciStatusActiveAction',
  'bciStatusSuspendAction',
  'bciStatusReplaceAction',
  'bciStatusHotAction',
  'bciStatusClosedAction',
  'forceTransactionAllowed',
  'activeIndicator',
  'summarizeOnSPStatement',
  'creationNoInTEN',
  'bacStatusDormantAction',
  'transStatusAfterSuccessful',
  'processSequence',
  'createTEN',
  'transactionFunctionAdditionalStandardFeeAmount',
  'transactionFunctionCollectAllFeesFirstBeforeTransaction',
  'transactionFunctionCollectImmediately',
  'transactionFunctionExcludePockets',
  'transactionFunctionExternalTransferAllowed',
  'transactionFunctionFeeReductionAllowed',
  'transactionFunctionForceValueToAccOnRejection',
  'transactionFunctionNoExecuteTENMessage',
  'transactionFunctionProcessAsReversal',
  'transactionFunctionSecurityCodeRequired',
  'transactionFunctionValueTransWithResponseCode',
  'typeOfInstruction',
  // 'relatedFINID', — handled in FIN_OBJECT_REF_ID_FIELDS (empty => 0 clears oref). 05/08/2026 PK.
  // 'feeInstructionFINID',
  'smsDTEID',
  'emailDTEID',
  'externalSMSDTEID',
  'externalEmailDTEID',
  'responseCodeClassification',
  'useGid',
  'useRno',
  'eraseTransactionCode',
  'authorizationMethod',
  // 'refATAAuthFINID',
  // 'refExecuteAuthFINID',
  'indefiniteHold',
  'replyToSMSDTEID',
  'typeOfHold',
  'newVersionRequired',
  'noOfDaysHoldLimit1',
  'noOfDaysHoldLimit2',
  'noOfDaysHoldLimit3',
  'noOfDaysOfIndefiniteHold',
  'holdLimit1Amount',
  'holdLimit2Amount',
  'holdLimit3Amount',
  'indefiniteHoldLimit',
  'onSuccessfulSMSDTEID',
  'onSuccessfulEmailDTEID',
];

/** FIN object-reference fields stored as numeric ids on the API — 0 clears the link. 05/08/2026 PK. */
const FIN_OBJECT_REF_ID_FIELDS: readonly string[] = [
  'relatedFINID',
  'feeInstructionFINID',
  'refATAAuthFINID',
  'refExecuteAuthFINID',
];

/** Integer fields bound to mat-checkbox in the FIN form — coerce boolean to 0/1. */
const FIN_INTEGER_CHECKBOX_FIELDS: readonly string[] = [
  'ignoreBankService',
  'forceFee',
  'indefiniteHold',
  'forceTransactionAllowed',
  'activeIndicator',
  'summarizeOnSPStatement',
  'cardRelatedTransaction',
];

const FIN_BOOLEAN_FIELDS: readonly string[] = [
  'stopActionAllowed',
  'tenWithTotalAmountZeroAllowed',
  'sendValueToRejectionAccount',
  'reversable',
  'generateAuthMac',
  'heldForRepresentment',
  'minimalCreditTrackingOnly',
  'createATRForAllResponses',
  'partialPaymentAllowed',
  'randomise',
  'resetActionAndEffDateDaily',
  'deductionCredit',
  'collectFeeOnCredit',
  'notificationEnabled',
  'allowIncomingRespCodeWithValue',
  'duplicationWarning',
  'createTDTNotTEN',
  'createATAandTENForAuth',
  'noOfDaysOnHoldIncomingAllowed',
  'allowTransferBalance',
  'testOnHoldAmountForAvailability',
  'relatedFINIDOnlyOnInvalidBAC',
  'createATRForErrorResponses',
  'displayOnReprosessBrowse',
  'bacRejectStatusNotAllowed',
  'transactionalFuntionCollectFeeOnMinBal',
  'transactionalFuntionProcessAsReversal',
  'transactionalFuntionNoExecuteTENMessage',
  'transactionalFuntionCollectAllFeesFirstBeforeTransaction',
];

const FIN_STRING_FIELDS: readonly string[] = [
  'instructionDescription',
  'internalDescription',
  'shortName',
  'recordIDFrom',
  'recordIDTo',
  'processedRecordID',
];

/** Derived or not applied by UpdateFinancialIns — omit from outbound body. */
const FIN_OMIT_FIELDS: readonly string[] = [
  'relatedInstructionNo',
  'relatedInstructionDescription',
  'recordDate',
  'recordTime',
  'FraudRules',
  'MaxConsecDaysMaxLimit',
  'MaxTransPHourSameMerchant',
  'MaxDeclinePerDay',
];

function isEmptyValue(value: unknown): boolean {
  return value === '' || value === null || value === undefined;
}

function coerceInteger(value: unknown, fromCheckbox = false): number | undefined {
  if (isEmptyValue(value)) {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return fromCheckbox ? (value ? 1 : 0) : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return undefined;
    }
    const n = Number(trimmed);
    return Number.isNaN(n) ? undefined : Math.trunc(n);
  }
  return undefined;
}

function coerceBoolean(value: unknown): boolean | undefined {
  if (isEmptyValue(value)) {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    if (lower === '' || lower === 'null' || lower === 'undefined') {
      return undefined;
    }
    if (lower === 'true' || lower === '1') {
      return true;
    }
    if (lower === 'false' || lower === '0') {
      return false;
    }
  }
  return undefined;
}

/**
 * Build a FINRequest-shaped JSON body safe for %JSONImport (create/update).
 */
export function normalizeFinancialInstructionForApi(
  body: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const key of FIN_STRING_FIELDS) {
    if (!(key in body)) {
      continue;
    }
    const v = body[key];
    if (isEmptyValue(v)) {
      continue;
    }
    out[key] = String(v);
  }

  const checkboxSet = new Set(FIN_INTEGER_CHECKBOX_FIELDS);

  for (const key of FIN_INTEGER_FIELDS) {
    if (!(key in body)) {
      continue;
    }
    const n = coerceInteger(body[key], checkboxSet.has(key));
    if (n !== undefined) {
      out[key] = n;
    }
  }

  for (const key of FIN_OBJECT_REF_ID_FIELDS) {
    if (!(key in body)) {
      continue;
    }
    const n = coerceInteger(body[key]);
    // if (n !== undefined) {
    //   out[key] = n !== undefined ? n : 0;
    // }
    // Omit empty FIN oref fields; only send positive ids or explicit 0 to clear. 06/08/2026 PK.
    if (n !== undefined) {
      out[key] = n;
    }
  }

  for (const key of FIN_BOOLEAN_FIELDS) {
    if (!(key in body)) {
      continue;
    }
    const b = coerceBoolean(body[key]);
    if (b !== undefined) {
      out[key] = b;
    }
  }

  for (const key of FIN_OMIT_FIELDS) {
    delete out[key];
  }

  return out;
}
